/**
 * Single source of truth for the product-expression snippets and the
 * expression field list offered by the three insert surfaces:
 *
 *  - toolbar "Expressions" dropdown   (components/remirror/editor-func.ts)
 *  - HTML source editor field menu    (components/remirror/RawHtmlEditor.tsx)
 *  - Fields sidebar                   (containers/designer/templates/ProductFieldsPanel .tsx)
 *
 * Before this module each surface built its own copy and they had drifted
 * (spacing differences, no table option, labels out of sync). Keep new
 * snippets here so all three stay identical.
 */
import { DataTypeName } from "../data/data-types";
import { EditorField } from "../data/editor";
import { DesignItemRef } from "./design.types";
import { CategoryRef } from "../products/product";

/**
 * Sentinel `content` values. A field whose `content` is one of these asks the
 * insert surface for a multi-line snippet instead of a plain `{{Token}}`.
 */
export const SNIPPET_VARIANTS_LIST = "$variants";
export const SNIPPET_VARIANTS_TABLE = "$variants-table";
export const SNIPPET_PRODUCT_LIST = "$product-list";
export const SNIPPET_CATEGORY_LIST = "$category-list";

/**
 * Snippets that are HTML (not plain text) and therefore must never be pushed
 * into the WYSIWYG editor as text — they go to the HTML source view.
 */
export const isHtmlSnippet = (content?: string) =>
  content === SNIPPET_VARIANTS_TABLE;

/** ` {{Color}} {{Size}}` — one leading space per category option. */
const optionTokens = (category?: CategoryRef) =>
  category?.options?.reduce((a, o) => `${a} {{${o.name}}}`, "") ?? "";

const escapeHtml = (text: string) =>
  text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/**
 * The flowing variants list — one line per variant. Returned as lines so each
 * surface can join them the way it inserts (paragraphs / newlines / one line).
 */
export const variantsListLines = (category?: CategoryRef): string[] => [
  "{{Variants}}",
  `{{Image:Fit:50:50}}${optionTokens(
    category
  )} {{SKU}} {{Price}} {{Description}}`,
  "{{/Variants}}",
];

export const productListLines = (): string[] => [
  "{{Products}}",
  "{{Name}} {{Code}} {{Price}}",
  "{{/Products}}",
];

export const categoryListLines = (): string[] => [
  "{{Categories}}",
  "{{Name}}",
  "{{/Categories}}",
];

/**
 * A real column table of the variants: one `<th>` per category option, plus
 * SKU and Price.
 *
 * The iterator has to be comment-wrapped (`<!--{{Variants}}-->`) because a bare
 * `{{Variants}}` text node inside a `<table>` gets foster-parented out of the
 * table by the browser's HTML parser, while comments survive. The V1 render
 * (`ContentParser.ProcessQueryBlock`) is a pure string match on
 * `{{Variants}}…{{/Variants}}` and simply carries the `-->`/`<!--` fragments
 * into the repeated block, which collapses to harmless empty `<!---->` comments
 * between the generated rows. This mirrors the pattern paying accounts already
 * hand-build in code view.
 */
export const variantsTableHtml = (category?: CategoryRef): string => {
  const columns = [
    "SKU",
    ...(category?.options?.map((o) => o.name) ?? []),
    "Price",
  ];
  const headers = columns
    .map((c) => `<th style="text-align: left;">${escapeHtml(c)}</th>`)
    .join("");
  const cells = columns.map((c) => `<td>{{${c}}}</td>`).join("");

  return [
    '<table style="width: 100%; border-collapse: collapse;">',
    `<thead><tr>${headers}</tr></thead>`,
    "<tbody>",
    "<!--{{Variants}}-->",
    `<tr>${cells}</tr>`,
    "<!--{{/Variants}}-->",
    "</tbody>",
    "</table>",
  ].join("\n");
};

/** Lines for a text snippet, or `null` when `content` is a plain `{{Token}}`. */
export const getSnippetLines = (
  content: string,
  category?: CategoryRef
): string[] | null => {
  switch (content) {
    case SNIPPET_VARIANTS_LIST:
      return variantsListLines(category);
    case SNIPPET_PRODUCT_LIST:
      return productListLines();
    case SNIPPET_CATEGORY_LIST:
      return categoryListLines();
    default:
      return null;
  }
};

/** What to insert into a plain-text/HTML target (source editor, new text box). */
export const getSnippetText = (
  content: string,
  category?: CategoryRef
): string => {
  if (content === SNIPPET_VARIANTS_TABLE) return variantsTableHtml(category);
  return getSnippetLines(content, category)?.join("\n") ?? content;
};

/** Same snippet collapsed onto one line — used where a preview label doubles as the value. */
export const getInlineSnippetText = (
  content: string,
  category?: CategoryRef
): string => {
  if (content === SNIPPET_VARIANTS_TABLE) return variantsTableHtml(category);
  return getSnippetLines(content, category)?.join(" ") ?? content;
};

export const EXPRESSION_DESCRIPTIONS: Record<string, string> = {
  [SNIPPET_VARIANTS_LIST]: "One line per variant, flowing down the box",
  [SNIPPET_VARIANTS_TABLE]: "Column table with a row per variant",
  [SNIPPET_PRODUCT_LIST]: "Repeat name, code and price for every product",
  [SNIPPET_CATEGORY_LIST]: "Repeat the name of every category",
};

/**
 * Expression field list for a design item's editor menus. Extracted from the
 * two identical `getItemFields` copies (designer board + Concierge preview).
 */
export const buildItemFields = (
  item: DesignItemRef,
  defaultCategory?: CategoryRef,
  productOnly?: boolean
): EditorField[] => {
  if (!item) return [];

  const isProductSink = item.di.subtype === "product" && !item.info?.product;

  const category = isProductSink
    ? defaultCategory
    : item.info?.product?.category;

  const categoryFields = [{ name: "Name" }, ...(category?.group.fields ?? [])];

  const fields: EditorField[] =
    categoryFields.map((f: { name: string; dataType?: DataTypeName }) => {
      const isImage = f.dataType === DataTypeName.Image;
      return {
        key: f.name,
        name: f.name,
        content: `{{${f.name}${isImage ? ":Fit:50:50" : ""}}}`,
      };
    }) ?? [];

  if (item.info.product?.category.options.length) {
    fields.push(
      { key: "1", name: null, content: null },
      {
        key: SNIPPET_VARIANTS_LIST,
        name: "Variants (list)",
        content: SNIPPET_VARIANTS_LIST,
        desc: EXPRESSION_DESCRIPTIONS[SNIPPET_VARIANTS_LIST],
      },
      {
        key: SNIPPET_VARIANTS_TABLE,
        name: "Variants (table)",
        content: SNIPPET_VARIANTS_TABLE,
        desc: EXPRESSION_DESCRIPTIONS[SNIPPET_VARIANTS_TABLE],
      }
    );
  }

  if (!item.info.product && !productOnly) {
    if (isProductSink) {
      fields.push({ key: "2", name: null, content: null });
    }
    fields.push(
      {
        key: SNIPPET_PRODUCT_LIST,
        name: "Product List",
        content: SNIPPET_PRODUCT_LIST,
        desc: EXPRESSION_DESCRIPTIONS[SNIPPET_PRODUCT_LIST],
      },
      {
        key: SNIPPET_CATEGORY_LIST,
        name: "Category List",
        content: SNIPPET_CATEGORY_LIST,
        desc: EXPRESSION_DESCRIPTIONS[SNIPPET_CATEGORY_LIST],
      }
    );
  }

  return fields;
};
